import { ErrorMessage } from "@/components/ErrorMessage";
import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { EditSecretForm, type EditSecretFormOnSubmitInput } from "@/components/secrets/EditSecretForm";
import { TrackLocations } from "@/hooks/use-segment";
import { useUpdateServiceAccountSecretValue } from "@/queries/service-account-secret-queries";
import type { PlainSecret } from "@/queries/secret-queries";
import { formatError } from "@/utils/errors";
import { useCallback, useState, type FC } from "react";
import type { ServiceAccountHeaders } from "@/api";

type Props = {
    secret: PlainSecret;
    serviceAccountId: string;
    headers: ServiceAccountHeaders;
    onClose: () => void;
};

export const ServiceAccountEditSecretModal: FC<Props> = ({ secret, serviceAccountId, headers, onClose }) => {
    const { toast } = useToast();
    const updateSecretValue = useUpdateServiceAccountSecretValue({ headers });

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!nextOpen) {
                onClose();
            }
        },
        [onClose],
    );

    const onSubmit = useCallback(
        async ({ secretValuePlaintext }: EditSecretFormOnSubmitInput) => {
            try {
                await updateSecretValue.mutateAsync({
                    serviceAccountId,
                    secretId: secret.id,
                    plaintextValue: secretValuePlaintext,
                });
                toast({
                    title: `${secret.name} was successfully updated`,
                });
                onClose();
            } catch (e) {
                toast({
                    title: "Failed to update secret",
                    description: formatError(e),
                });
            }
        },
        [serviceAccountId, secret.id, secret.name, updateSecretValue, onClose, toast],
    );

    const [disabled, setDisabled] = useState<boolean>(true);

    return (
        <Dialog open onOpenChange={handleOpenChange}>
            <DialogContent
                className="max-w-[600px]"
                data-testid="service-account-edit-secret-modal-content"
                data-track-location={TrackLocations.EditSecretModal}
            >
                <DialogHeader>
                    <DialogTitle>Edit secret</DialogTitle>
                    <DialogDescription>
                        Update the secret value. File path and secret type cannot be changed.
                    </DialogDescription>
                </DialogHeader>

                <DialogBody className="overflow-x max-w-full">
                    <ErrorMessage
                        error={updateSecretValue.error}
                        data-testid="error-message-service-account-secret-update-failed"
                        className="text-center"
                    />
                    <EditSecretForm
                        formName="service-account-edit-secret-form"
                        secret={secret}
                        onSubmit={onSubmit}
                        onDisabledChange={setDisabled}
                    />
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button type="button" variant="outline">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        variant="primary"
                        form="service-account-edit-secret-form"
                        loading={updateSecretValue.isPending}
                        disabled={updateSecretValue.isPending || disabled}
                        data-testid="service-account-edit-secret-submit-button"
                        data-tracking-id="confirm-update-secret-service-account-edit-secret-modal"
                    >
                        Update
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
